// src/hooks/useDashboard.js - Hook for accessing dashboard data from DashboardStore
import useDashboardStore from '../stores/DashboardStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing dashboard analytics data from DashboardStore
 *
 * Store initialization is handled at app level in SocketContext.
 * Uses proper Zustand selectors for optimal re-renders.
 *
 * @returns {object} Dashboard data and operations
 */
export const useDashboard = () => {
  const socket = useSocket();

  // Use individual selectors instead of destructuring entire store
  // This ensures React re-renders only when specific properties change

  // Dashboard data
  const dashboardStats = useDashboardStore(state => state.dashboardStats);
  const dashboardLoading = useDashboardStore(state => state.dashboardLoading);
  const dashboardError = useDashboardStore(state => state.dashboardError);

  // Dashboard operations (functions don't change, so we can get them once)
  const loadDashboardStats = useDashboardStore(state => state.loadDashboardStats);
  const refreshDashboardStats = useDashboardStore(state => state.refreshDashboardStats);
  const clearDashboardError = useDashboardStore(state => state.clearDashboardError);

  // Connection state
  const isConnected = useDashboardStore(state => state.isConnected);

  // Internal initialization state (for fallback check)
  const _initialized = useDashboardStore(state => state._initialized);

  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useDashboard: Store not initialized at app level, trying fallback');
    socket.initializeStore('DashboardStore');
  }

  // Return interface
  return {
    // Dashboard data
    dashboardStats,
    dashboardLoading,
    dashboardError,

    // Dashboard operations
    loadDashboardStats,
    refreshDashboardStats,
    clearDashboardError,

    // Connection state
    isConnected: socket.isAuthenticated,

    // Legacy aliases for backward compatibility
    loading: dashboardLoading,
    error: dashboardError,
    stats: dashboardStats
  };
};
